import type { Response } from "@/types"

import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import type {
  AIDraftResponse,
  AIDraftSummaryResponse,
  DismissAIDraftRequest,
  GenerateAIDraftRequest,
} from "./histories"

export interface HistoryAIDraftTarget {
  kind: "history"
  historyId: number
}

export interface ThreadAIDraftTarget {
  kind: "thread"
  organizationId: string
  workspaceId: string
  threadKey: string
}

export type AIDraftTarget = HistoryAIDraftTarget | ThreadAIDraftTarget

export const historyAIDraftTarget = (
  historyId: number
): HistoryAIDraftTarget => ({ kind: "history", historyId })

export const threadAIDraftTarget = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
): ThreadAIDraftTarget => ({
  kind: "thread",
  organizationId,
  workspaceId,
  threadKey,
})

export const aiDraftTargetKey = (target: AIDraftTarget) =>
  target.kind === "history"
    ? `history:${target.historyId}`
    : `thread:${target.organizationId}:${target.workspaceId}:${target.threadKey}`

export const aiDraftTargetBasePath = (target: AIDraftTarget) =>
  target.kind === "history"
    ? `/histories/${target.historyId}/ai-drafts`
    : `/organizations/${target.organizationId}/workspaces/${target.workspaceId}/threads/${target.threadKey}/ai-drafts`

export const useGenerateAIDraftApi = (target: AIDraftTarget) =>
  useCodeerApi<Response<AIDraftResponse>, GenerateAIDraftRequest>(
    aiDraftTargetBasePath(target),
    FetchMethod.Post
  )

export const useListAIDraftsApi = (target: AIDraftTarget) =>
  useCodeerApi<Response<AIDraftSummaryResponse[]>>(
    aiDraftTargetBasePath(target),
    FetchMethod.Get
  )

export const useGetAIDraftApi = (target: AIDraftTarget, draftId: number) =>
  useCodeerApi<Response<AIDraftResponse>>(
    `${aiDraftTargetBasePath(target)}/${draftId}`,
    FetchMethod.Get
  )

export const useApplyAIDraftApi = (target: AIDraftTarget, draftId: number) =>
  useCodeerApi<Response<AIDraftResponse>>(
    `${aiDraftTargetBasePath(target)}/${draftId}/apply`,
    FetchMethod.Post
  )

export const useDismissAIDraftApi = (target: AIDraftTarget, draftId: number) =>
  useCodeerApi<Response<AIDraftResponse>, DismissAIDraftRequest>(
    `${aiDraftTargetBasePath(target)}/${draftId}/dismiss`,
    FetchMethod.Post
  )
